/**
 * WriterFeedbackPanel — admin view for messages submitted via the
 * floating Suggestion Box widget in The Loom workspace.
 *
 * Features:
 *  - Paginated list (50/page) with date, name, email hint, context, and message
 *  - Click a row to expand the full message
 *  - Delete button per message (soft confirmation via toast)
 *  - Auto-refresh on mount; manual refresh button
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  MessageSquare, Trash2, RefreshCw, ChevronDown, ChevronRight,
  Inbox, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

async function fetchFeedback(page = 1) {
  const resp = await fetch(`${API}/admin/feedback?page=${page}&limit=50`, {
    credentials: 'include',
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json();
}

async function deleteFeedback(id) {
  const resp = await fetch(`${API}/admin/feedback/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json();
}

function RelativeTime({ iso }) {
  if (!iso) return <span className="text-muted-foreground">—</span>;
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return <span>{mins < 2 ? 'just now' : `${mins}m ago`}</span>;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return <span>{hrs}h ago</span>;
  return <span>{d.toLocaleDateString()}</span>;
}

function FeedbackRow({ item, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (deleting) return;
    setDeleting(true);
    try {
      await onDelete(item.id);
      toast.success('Feedback removed.');
    } catch {
      toast.error('Could not delete — try again.');
      setDeleting(false);
    }
  };

  return (
    <>
      <tr
        className="cursor-pointer hover:bg-muted/30 transition-colors border-b border-border"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`feedback-row-${item.id}`}
      >
        <td className="px-3 py-2 w-5">
          {expanded
            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
        </td>
        <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
          <RelativeTime iso={item.submitted_at} />
        </td>
        <td className="px-3 py-2 text-xs font-medium max-w-[120px] truncate" title={item.name}>
          {item.name || '—'}
        </td>
        <td className="px-3 py-2 text-xs text-muted-foreground max-w-[80px] truncate" title={item.email_hint}>
          {item.email_hint || '—'}
        </td>
        <td className="px-3 py-2">
          {item.context && (
            <Badge variant="outline" className="text-[9px] h-4 px-1.5 font-normal">
              {item.context}
            </Badge>
          )}
        </td>
        <td className="px-3 py-2 text-xs max-w-[240px] truncate text-foreground">
          {item.message}
        </td>
        <td className="px-3 py-2 w-8">
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            title="Delete this feedback"
            className="opacity-30 hover:opacity-100 transition-opacity"
            data-testid={`feedback-delete-${item.id}`}
          >
            {deleting
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Trash2 className="h-3.5 w-3.5 text-destructive" />}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-muted/20 border-b border-border" data-testid={`feedback-expanded-${item.id}`}>
          <td colSpan={7} className="px-5 py-3">
            <div className="rounded-md border border-border bg-background p-3 text-xs leading-relaxed whitespace-pre-wrap text-foreground">
              {item.message}
            </div>
            {item.context && (
              <p className="mt-2 text-[10px] text-muted-foreground">
                Panel context at time of submission: <strong>{item.context}</strong>
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function WriterFeedbackPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const data = await fetchFeedback(p);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (err) {
      toast.error('Could not load feedback. Check admin permissions.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = useCallback(async (id) => {
    await deleteFeedback(id);
    setItems((prev) => prev.filter((x) => x.id !== id));
    setTotal((t) => Math.max(0, t - 1));
  }, []);

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="p-4 space-y-4" data-testid="writer-feedback-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-rose-700" />
          <h2 className="text-sm font-semibold">Writer Feedback</h2>
          {total > 0 && (
            <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
              {total} message{total !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => load(page)}
          disabled={loading}
          data-testid="feedback-refresh-btn"
          className="h-7 px-2 text-xs gap-1.5"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </Button>
      </div>

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-12 text-center" data-testid="feedback-empty">
          <Inbox className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm font-medium text-muted-foreground">No feedback yet</p>
          <p className="text-xs text-muted-foreground/60">
            Writers can submit suggestions via the "?" button in the Loom workspace.
          </p>
        </div>
      )}

      {/* Table */}
      {items.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-left border-collapse" data-testid="feedback-table">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-3 py-2 w-5" />
                <th className="px-3 py-2 text-[10px] font-semibold uppercase text-muted-foreground whitespace-nowrap">When</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase text-muted-foreground">Name</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase text-muted-foreground">Email</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase text-muted-foreground">Context</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase text-muted-foreground">Message</th>
                <th className="px-3 py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <FeedbackRow key={item.id} item={item} onDelete={handleDelete} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Page {page} of {totalPages} · {total} total</span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => load(page - 1)}
              className="h-7 px-2 text-xs"
            >Prev</Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => load(page + 1)}
              className="h-7 px-2 text-xs"
            >Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
